import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Store } from '@ngrx/store';
import { ConnectionState } from 'common/store/genesys-cloud/conversations/common/models/connection-state.enum';
import { ThemeService } from 'common/theme/theme.service';
import { combineLatest, from, interval, Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, scan, switchMap, take, takeUntil, throttleTime } from 'rxjs/operators';
import { selectLastEmailMessageByConversationId } from 'common/store/genesys-cloud/conversations/emails/selectors/email-message.selectors';
import { EmailConversation } from 'common/store/genesys-cloud/conversations/emails/models/email-conversation.model';
import { openConversationWorkItem } from 'common/store/genesys-cloud/conversations/common/actions/conversation-work-item.actions';
import { acceptEmailConversation } from 'common/store/genesys-cloud/conversations/emails/actions/email-conversation.actions';
import { selectEmailConversationById, selectHasEmailError } from 'common/store/genesys-cloud/conversations/emails/selectors/email-conversation.selectors';
import { selectContactFullNameById } from 'common/store/genesys-cloud/external-contacts/selectors/external-contact.selectors';

interface SelectConversationEvent {
  conversation: EmailConversation;
  focus: boolean;
}

@Component({
  selector: 'app-work-item-genesys-email-list-item',
  templateUrl: './workitem-genesys-email-list-item.component.html',
  styleUrls: ['./workitem-genesys-email-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class WorkItemGenesysEmailListItemComponent implements OnDestroy {
  private static readonly alertingTimeoutRefreshIntervalInMilliSeconds = 1000;
  static readonly SelectActionThrottleTimeMs = 300;
  private readonly conversationIdSubject = new ReplaySubject<string>(1);
  private readonly onSelectSubject = new Subject<SelectConversationEvent>();
  private readonly onDestroySubject = new Subject<void>();

  @Input()
  set conversationId(newValue: string) {
    this.conversationIdSubject.next(newValue);
  }

  @Input() isOpened = false;
  @Input() isFocused = false;

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  private readonly conversationId$ = this.conversationIdSubject.pipe(
    filter(conversationId => !!conversationId),
    distinctUntilChanged()
  );
  readonly emailConversation$ = this.conversationId$.pipe(
    switchMap(conversationId => this.store.select(selectEmailConversationById(conversationId)))
  );

  readonly email$ = this.conversationId$.pipe(
    mergeMap(id => from(this.store.select(selectLastEmailMessageByConversationId(id))))
  );

  readonly textBody$ = this.email$.pipe(
    map(email => email?.textBody)
  );

  readonly subject$ = this.email$.pipe(
    map(email => email?.subject)
  );

  readonly emptyProactive$ = this.emailConversation$.pipe(
    map(c => c.isProactiveEmail && !c.lastEmailMessageId)
  );

  readonly newItem$ = this.emailConversation$.pipe(
    map(conversation => conversation.agentParticipant?.emails?.some(e => e.state === ConnectionState.Alerting)),
    distinctUntilChanged()
  );

  readonly name$ = this.emailConversation$.pipe(
    map(conversation => conversation.customerParticipant?.name || conversation.customerParticipant?.address),
    distinctUntilChanged()
  );

  readonly externalContactFullName$ = this.emailConversation$.pipe(
    map(conversation => conversation.customerParticipant?.externalContactId),
    distinctUntilChanged(),
    switchMap(contactId => this.store.select(selectContactFullNameById(contactId)))
  );

  readonly themeCssClassName$ = this.conversationId$.pipe(
    map(conversationId => this.themeService.getThemeId(conversationId)),
    map(themeId => this.themeService.getThemeCssClassName(themeId))
  );

  readonly itemStyle$ = combineLatest([this.themeCssClassName$, this.newItem$]).pipe(
    map(([className, newItem]) => `${className} ${newItem ? 'new-item' : ''}`)
  );

  readonly connectedTime$ = this.emailConversation$.pipe(
    map(conversation => conversation.customerParticipant?.connectedTime)
  );

  readonly isSecondsRemainingVisible$ = this.emailConversation$.pipe(map((item) => item.isAcceptable));

  readonly emailAlertingTimeout$: Observable<number> = this.emailConversation$.pipe(
    filter(conv => !!conv),
    distinctUntilChanged((prev, cur) => prev.isAcceptable === cur.isAcceptable),
    switchMap(conv => this.startRemainingTimer(conv.agentParticipant.alertingTimeoutMs))
  );

  constructor(private readonly themeService: ThemeService,
              private readonly store: Store,
              private readonly changeDetectorRef: ChangeDetectorRef) {
    this.onSelectSubject.pipe(
      throttleTime(WorkItemGenesysEmailListItemComponent.SelectActionThrottleTimeMs),
      takeUntil(this.onDestroySubject)
    ).subscribe(onSelectEvent => this.dispatchConversationAction(onSelectEvent));
  }

  selectConversation(conversation: EmailConversation, focus: boolean): void {
    this.onSelectSubject.next({ conversation, focus });
  }

  readonly error$ = this.conversationId$.pipe(
    switchMap(conversationId => this.store.select(selectHasEmailError(conversationId)))
  );

  timerTextChanged() {
    this.changeDetectorRef.detectChanges();
  }

  private dispatchConversationAction({ conversation, focus }: SelectConversationEvent): void {
    const action = conversation.isAcceptable
      ? acceptEmailConversation({ conversationId: conversation.id, focus })
      : openConversationWorkItem({ conversationId: conversation.id, focus });
    this.store.dispatch(action);
  }

  startRemainingTimer(alertingTimeout: number): Observable<number> {
    return interval(WorkItemGenesysEmailListItemComponent.alertingTimeoutRefreshIntervalInMilliSeconds)
      .pipe(
        scan(acc => --acc, alertingTimeout / WorkItemGenesysEmailListItemComponent.alertingTimeoutRefreshIntervalInMilliSeconds),
        take(alertingTimeout / WorkItemGenesysEmailListItemComponent.alertingTimeoutRefreshIntervalInMilliSeconds)
      );
  }
}
